import React from "react";
import { useImage } from "react-image";

import { CircularProgress } from "@mui/material";

import { Image } from "@models/Image";
import { cn } from "@utils/tailwind";

import { cloudinary } from "./cloudinary";
import imageSizeOptions, { ImageSize } from "./imageSizeOptions";

const FALLBACK_IMAGE_URL =
  "https://res.cloudinary.com/brandhub/image/upload/v1685722984/prod/Brandhub/no-image-square_iefxuz.png";

type CldImageProps = Omit<React.HTMLProps<HTMLImageElement>, "size"> & {
  src?: string;
  publicId?: string;
  image?: Image | null;
  size?: ImageSize;
};

const ImageLoadingFallback = ({ className }: { className?: string }) => (
  <div className={cn("flex items-center justify-center w-8 h-8", className)}>
    <CircularProgress size={16} className="text-neutral-300" />
  </div>
);

const LazyImage = ({ src, ...props }: CldImageProps & { src: string }) => {
  const {
    src: renderSrc,
    isLoading,
    error,
  } = useImage({
    srcList: [src, FALLBACK_IMAGE_URL],
    useSuspense: false,
  });

  if (isLoading) {
    return <ImageLoadingFallback className={props.className} />;
  }

  return <img src={error ? FALLBACK_IMAGE_URL : renderSrc} alt="" {...props} />;
};

const CldImage = ({
  src: providedSrc,
  image,
  publicId,
  size,
  ...props
}: CldImageProps) => {
  const urlOptions = size ? imageSizeOptions[size] : {};
  const cloudinaryId = publicId ?? image?.cloudinaryId;
  const src =
    providedSrc ??
    (cloudinaryId
      ? cloudinary.url(cloudinaryId, urlOptions)
      : FALLBACK_IMAGE_URL);

  return <LazyImage src={src} {...props} />;
};

export default CldImage;
